import { Virtuoso, GroupedVirtuoso } from "react-virtuoso";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { Button } from "../button-with-loading";
import EmptyPlaceholder from "../empty-placeholder";
import { LoadMoreButton } from "../load-more-button";
import { useVirtuosoWrapperState } from "./hook";
import { VirtuosoWrapperProps } from "./types";

/**
 * VirtuosoWrapper - Generic grouped virtual list component
 *
 * A flexible, type-safe virtual list component that supports:
 * - Flat lists or grouped lists (by date, category, etc.)
 * - Scroll preservation with "new items available" notification
 * - Pagination with customizable footer
 * - Empty states
 * - Full TypeScript generics support
 *
 * @example
 * ```tsx
 * // Simple flat list
 * <VirtuosoWrapper
 *   data={items}
 *   renderItem={(item) => <ItemComponent item={item} />}
 *   pagination={{ hasMore, isLoadingMore, loadNext }}
 * />
 *
 * // Grouped by date
 * <VirtuosoWrapper
 *   data={items}
 *   grouping={{
 *     groupBy: (items) => GroupingUtils.groupByDate(items, item => item.date)
 *   }}
 *   renderItem={(item) => <ItemComponent item={item} />}
 *   renderGroupHeader={(name) => <SectionHeader title={name} />}
 * />
 * ```
 */
export function VirtuosoWrapper<T>({
  data,
  grouping,
  scrollManager,
  pagination,
  renderItem,
  renderGroupHeader,
  renderEmpty,
  components,
  className = "flex-1 scrollbar-thin",
  virtuosoProps = {},
}: VirtuosoWrapperProps<T>) {
  const {
    virtuosoRef,
    firstItemIndex,
    handleAtTopChange,
    showUpdateNotification,
    scrollToTop,
    groupedData,
    isGrouped,
    getRenderContext,
  } = useVirtuosoWrapperState({
    data,
    grouping,
    scrollManager,
    isLoadingMore: pagination?.isLoadingMore,
  });

  // Notification button configuration
  const notificationText =
    scrollManager?.notificationText ?? "New items available";
  const notificationClassName =
    scrollManager?.notificationClassName ??
    "bg-squawk-blue border !rounded-full px-4 py-1 text-xs font-normal text-white shadow-md";
  const NotificationIcon = scrollManager?.notificationIcon ?? ArrowUpIcon;

  // Footer rendering logic
  const renderFooter = () => {
    if (!pagination) return null;

    // Use custom footer if provided
    if (pagination.footer?.renderCustom) {
      return pagination.footer.renderCustom({
        hasMore: pagination.hasMore,
        isLoading: pagination.isLoadingMore,
        loadNext: pagination.loadNext,
      });
    }

    // Show loading state
    if (pagination.hasMore) {
      if (pagination.footer?.renderLoading) {
        return pagination.footer.renderLoading();
      }
      return (
        <LoadMoreButton
          loading={pagination.isLoadingMore}
          loadNextPage={pagination.loadNext}
        />
      );
    }

    // Show end state
    if (pagination.footer?.renderEnd) {
      return pagination.footer.renderEnd();
    }

    return (
      <div className="flex justify-center py-4 text-gray-400 text-sm">
        End of results
      </div>
    );
  };

  // Empty state rendering
  const renderEmptyState = () => {
    if (renderEmpty) {
      return renderEmpty(pagination?.isLoadingMore ?? false);
    }
    return (
      <EmptyPlaceholder
        message="No items to display"
        isLoading={pagination?.isLoadingMore ?? false}
      />
    );
  };

  // Common Virtuoso components
  const virtuosoComponents = {
    EmptyPlaceholder: renderEmptyState,
    Footer: renderFooter,
    ...components,
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* New items notification */}
      {scrollManager?.enabled && showUpdateNotification && (
        <div className="flex justify-center absolute top-2 z-40 w-full">
          <Button
            className={notificationClassName}
            onClick={scrollToTop}
            renderIcon={() => <NotificationIcon className="size-3" />}
          >
            {notificationText}
          </Button>
        </div>
      )}

      {/* Render grouped or flat list */}
      {isGrouped ? (
        <GroupedVirtuoso
          ref={virtuosoRef}
          className={className}
          groupCounts={groupedData.groupCounts}
          groupContent={(index) => {
            // Hide first group header if configured
            if (grouping?.hideFirstGroupHeader && index === 0) {
              return <div className="h-[1px]" />;
            }

            // Use custom group header renderer
            if (renderGroupHeader) {
              return renderGroupHeader(
                groupedData.groups[index],
                index,
                groupedData.groupCounts[index]
              );
            }

            // Default group header
            return (
              <div className="sticky top-0 bg-gray-800 px-2 py-1 text-xs font-semibold text-gray-400 z-10">
                {groupedData.groups[index]}
              </div>
            );
          }}
          itemContent={(index) => {
            // Validate index first
            if (typeof index !== "number" || isNaN(index)) {
              console.error("VirtuosoWrapper: Invalid index received:", index);
              return null;
            }

            // Adjust index based on firstItemIndex for scroll preservation
            const actualIndex = scrollManager?.enabled
              ? index - firstItemIndex
              : index;

            // Validate actualIndex
            if (
              isNaN(actualIndex) ||
              actualIndex < 0 ||
              actualIndex >= groupedData.items.length
            ) {
              console.error("VirtuosoWrapper: Invalid actualIndex:", {
                actualIndex,
                index,
                firstItemIndex,
                itemsLength: groupedData.items.length,
                scrollEnabled: scrollManager?.enabled,
              });
              return null;
            }

            const item = groupedData.items[actualIndex];
            if (!item) {
              console.error(
                "VirtuosoWrapper: No item found at actualIndex:",
                actualIndex
              );
              return null;
            }

            const context = getRenderContext(actualIndex);
            return renderItem(item, actualIndex, context);
          }}
          /*
            IMPORTANT: Use conditional spread to omit firstItemIndex/atTopStateChange props
            when scroll manager is disabled. DO NOT pass undefined - it causes GroupedVirtuoso
            to generate NaN indices in its internal calculations.
          */
          {...(scrollManager?.enabled && {
            firstItemIndex: firstItemIndex,
            atTopStateChange: handleAtTopChange,
          })}
          components={virtuosoComponents}
          {...virtuosoProps}
        />
      ) : (
        <Virtuoso
          ref={virtuosoRef}
          className={className}
          totalCount={groupedData.items.length}
          data={groupedData.items}
          itemContent={(index, item) => {
            // Adjust index based on firstItemIndex for scroll preservation
            const actualIndex = scrollManager?.enabled
              ? index - firstItemIndex
              : index;

            const context = getRenderContext(actualIndex);
            return renderItem(item, actualIndex, context);
          }}
          /*
            IMPORTANT: Use conditional spread to omit firstItemIndex/atTopStateChange props
            when scroll manager is disabled. DO NOT pass undefined - it causes Virtuoso
            to generate NaN indices in its internal calculations.
          */
          {...(scrollManager?.enabled && {
            firstItemIndex: firstItemIndex,
            atTopStateChange: handleAtTopChange,
          })}
          components={virtuosoComponents}
          {...virtuosoProps}
        />
      )}
    </div>
  );
}
